//NOTE: If you need to monitor session outside of keycloak, use this.
//Import to know that if you are making (or polling) GQL calls, keycloak will consider this an activity and reset idle timeout (even if user is not doing anything).
//This service will not care if GQL calls are being made, it detects user DOM events.
//Uses local storage to handle multiple browser timeouts (aligns with timeout rules that keycloak uses). Same tabs sessions timeout together, separate browsers do not.
import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { LocalStorageKeys } from '@common/constants';
import { Utilities } from '@common/utils';
import { EMPTY, fromEvent, interval, merge, Subject, Subscription } from 'rxjs';
import { map, switchMap, startWith, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '@govalta-emu/keycloak-auth-service';

const SECONDS_TO_MILLISECONDS = 1000;

//Options for what DOM events will trigger session timer reset
export interface ResetEventOptions {
  mousemove?: boolean;
  keydown?: boolean;
  click?: boolean;
  scroll?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class IdleMonitor implements OnDestroy {
  private stop$ = new Subject<void>();
  private activity$ = new Subject<void>();
  private idleTimeoutSeconds = 30 * 60;
  private intervalMs = 1000;
  private timeoutTriggered = false;
  private startTimeMs = 0;
  private domEventsSub?: Subscription;
  private timerSub?: Subscription;
  public sessionTimeRemainingSeconds$ = new Subject<number>();
  private throttledResetTimer: () => void;
  private throttledRefreshSessions: () => void;
  private throttledReload: () => void;
  private localStorageSub?: Subscription;
  private redirectOnly = false;
  private lastRefreshed = new Date();
  private fiveMinutesInSeconds = 300;

  //default options, movemove off because this may not always be an intentional action
  private defaultResetEventOptions = {
    mousemove: false,
    keydown: true,
    click: true,
    scroll: true,
  } as ResetEventOptions;

  constructor(private ngZone: NgZone, private authenticationService: AuthenticationService) {}

  ngOnDestroy(): void {
    // Final cleanup at end of service lifecycle
    this.stop(); // unsub all current subs
    this.stop$.complete();
    this.activity$.complete();
    this.sessionTimeRemainingSeconds$.complete();
  }

  monitorIdle(
    timeoutSeconds: number,
    intervalSeconds: number,
    onTimeout: (redirectOnly?: boolean) => void,
    resetEventOptions: ResetEventOptions = null,
    debugMode = false
  ) {
    // Ensure any previous run is torn down before starting a new one
    this.stop();

    //ensure the local storage key is unique based on token auth time
    //this will completely avoid any components in the same app using the same local storage key
    Utilities.setLocalStorageValue(LocalStorageKeys.IdleMonitorTimeout.key, null);

    this.idleTimeoutSeconds = timeoutSeconds;
    this.intervalMs = intervalSeconds * SECONDS_TO_MILLISECONDS;
    this.timeoutTriggered = false;
    this.startTimeMs = Date.now();

    //start
    if (debugMode) {
      this.showTimeRemaining(this.idleTimeoutSeconds);
    }

    // Dynamically build DOM event observables
    const eventStreams = [];
    if (!resetEventOptions) {
      resetEventOptions = this.defaultResetEventOptions; //if null, use default options
    }
    if (resetEventOptions?.mousemove) {
      eventStreams.push(fromEvent(document, 'mousemove'));
    }
    if (resetEventOptions?.keydown) {
      eventStreams.push(fromEvent(document, 'keydown'));
    }
    if (resetEventOptions?.click) {
      eventStreams.push(fromEvent(document, 'click'));
    }
    if (resetEventOptions?.scroll) {
      eventStreams.push(fromEvent(document, 'scroll'));
    }

    // Create throttled function ONCE
    // this.throttledResetTimer = Utilities.throttle(this.resetTimer.bind(this), this.intervalMs);
    this.throttledReload = Utilities.throttle(() => window.location.reload(), this.intervalMs);
    this.throttledResetTimer = Utilities.throttle(() => this.resetTimer(debugMode), this.intervalMs);
    this.throttledRefreshSessions = Utilities.throttle(() => {
      //trigger idle reset on other browsers
      this.lastRefreshed = new Date();
      Utilities.setLocalStorageValue(LocalStorageKeys.IdleMonitorLastRefreshed.key, this.lastRefreshed);
      // this.authenticationService.refreshToken(this.fiveMinutesInSeconds);
      this.authenticationService.refreshToken(this.fiveMinutesInSeconds).then((refreshedToken) => {
        if (refreshedToken === null) {
          // cannot get refresh token, user is no longer authenticated
          this.throttledReload();
        }
      });
    }, this.intervalMs);

    // const domEvents$ = eventStreams.length > 0 ? merge(...eventStreams) : new Subject<void>();
    const domEvents$ = eventStreams.length > 0 ? merge(...eventStreams) : EMPTY;

    this.ngZone.runOutsideAngular(() => {
      // Any user activity triggers reset
      // this.domEventsSub = domEvents$.subscribe(() => this.throttledResetTimer());
      this.domEventsSub = domEvents$.subscribe(() => {
        this.throttledResetTimer();

        //trigger idle reset on other browsers
        this.throttledRefreshSessions();
      });

      // (Re)start the ticking interval whenever activity$ emits (or immediately at start)
      this.timerSub = this.activity$
        .pipe(
          startWith(null),
          switchMap(() =>
            interval(this.intervalMs).pipe(
              map(() => {
                const elapsedMs = Date.now() - this.startTimeMs;
                return Math.max(this.idleTimeoutSeconds - elapsedMs / 1000, 0);
              }),
              // Stop current interval when a reset happens (activity$ emits)
              takeUntil(this.activity$)
            )
          ),
          // Stop everything for this run when stop$ emits
          takeUntil(this.stop$)
        )
        .subscribe((remaining) => {
          if (debugMode) {
            this.showTimeRemaining(remaining);
          }

          if (remaining <= 0 && !this.timeoutTriggered) {
            this.timeoutTriggered = true;
            if (debugMode) {
              console.log('Force timeout on all browser sessions');
            }
            if (!this.redirectOnly) {
              this.ngZone.run(() => onTimeout());
            }
            //logout other browser sessions
            //this is now running on session-timeout-page.component.ts. We can guarentee the logout complete once the first request hits that page
            // Utilities.setLocalStorageValue(this.localStorageKey, true);
          }

          this.sessionTimeRemainingSeconds$.next(remaining);
        });
    });

    // ---Listen for changes in localStorage from other tabs ---
    this.localStorageSub = fromEvent<StorageEvent>(window, 'storage').subscribe((event) => {
      if (event.key === LocalStorageKeys.IdleMonitorTimeout.key && event.newValue === 'true') {
        // ---Timeout: Detected ---
        this.redirectOnly = true; //prevent multiple sign outs
        if (debugMode) {
          console.log('Timeout detected on another browser session. Logging out...');
        }
        // Immediately push remaining time to 0
        this.sessionTimeRemainingSeconds$.next(0);
        if (!this.timeoutTriggered) {
          this.timeoutTriggered = true;
          this.ngZone.run(() => onTimeout(this.redirectOnly));
        }
      } else if (event.key === LocalStorageKeys.IdleMonitorLastRefreshed.key) {
        // --- Refresh: Detected ---
        const newValueDate = new Date(event.newValue.replace(/"/g, '')); // Convert string to Date
        if (newValueDate > this.lastRefreshed) {
          // Compare the dates
          if (debugMode) {
            console.log('Refresh detected on another browser session. Resetting idle time...');
          }
          this.lastRefreshed = newValueDate; // Update lastRefreshed
          this.resetTimer(debugMode);
        }
      }
    });
  }

  /** Reset idle timer manually (e.g., after refresh token) */
  async resetTimer(debugMode = false) {
    if (debugMode) {
      console.log('--- Activity detected. Reset idle timer ---');
      const refreshedToken = await this.authenticationService.refreshToken(this.fiveMinutesInSeconds);
      // console.log(refreshedToken);
      if (refreshedToken === null) {
        //cannot get refresh token, user is no longer authenticated
        this.throttledReload(); //reload will force page lifecycle and authguard to authenticate request correctly. will either get a new token or kick to timeout
      }
      this.showTimeRemaining(this.idleTimeoutSeconds);
    }
    this.startTimeMs = Date.now();
    this.timeoutTriggered = false;
    this.activity$.next(); // triggers a new interval via switchMap/takeUntil
    this.sessionTimeRemainingSeconds$.next(this.idleTimeoutSeconds);
  }

  /** Stop current monitoring run and clean up subscriptions */
  stop() {
    // Signal the pipeline to complete for this run
    this.stop$.next();

    // Unsubscribe explicit subscriptions
    this.domEventsSub?.unsubscribe();
    this.domEventsSub = undefined;

    this.timerSub?.unsubscribe();
    this.timerSub = undefined;

    this.localStorageSub?.unsubscribe();
    this.localStorageSub = undefined;
  }

  showTimeRemaining(timeInSeconds: number) {
    const timeRemaining =
      timeInSeconds > 120 ? (timeInSeconds / 60).toFixed(2) + ' minutes' : timeInSeconds.toFixed(0) + ' seconds';
    console.log('Idle Time Remaining: ' + timeRemaining);
  }
}
